import { useToast } from '@1trading/react/hooks/use-toast';
import { HTTPError } from 'ky';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PERIODIC_REVIEW_PATHS } from '@/features/periodic-review/constants';
import { useProfilePeriodicReviewConfirmationMutation } from '@/hooks/queries/use-profile-periodic-review';

// Where the customer can put right what stopped their review being confirmed
const PATH_BY_ERROR: Record<string, string> = {
  CONCAT_GENERATION_FAILED: PERIODIC_REVIEW_PATHS.PERSONAL_INFORMATION,
  NIDN_IDENTIFIER_TYPE_NOT_SUPPORTED: PERIODIC_REVIEW_PATHS.NATIONAL_CLIENT_IDENTIFIER,
  PERIODIC_REVIEW_AML_REQUIRED: PERIODIC_REVIEW_PATHS.AML_QUESTIONNAIRE,
  PERIODIC_REVIEW_FCA_QUESTIONNAIRE_REQUIRED: PERIODIC_REVIEW_PATHS.FCA_QUESTIONNAIRE,
  PERIODIC_REVIEW_NATIONAL_CLIENT_IDENTIFIER_REQUIRED: PERIODIC_REVIEW_PATHS.NATIONAL_CLIENT_IDENTIFIER,
  PERIODIC_REVIEW_PERSONAL_IDENTITY_REQUIRED: PERIODIC_REVIEW_PATHS.PERSONAL_INFORMATION,
  PERIODIC_REVIEW_TAX_RESIDENCY_REQUIRED: PERIODIC_REVIEW_PATHS.PERSONAL_INFORMATION,
};

const getErrorPath = async (error: unknown) => {
  if (!(error instanceof HTTPError)) return undefined;

  const body = await error.response.json<{ error?: string }>().catch(() => null);

  return body?.error ? PATH_BY_ERROR[body.error] : undefined;
};

// PeriodicReviewGuard moves the customer on once their review has been confirmed
export const useConfirmPeriodicReview = () => {
  const { isPending, mutate } = useProfilePeriodicReviewConfirmationMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const confirm = useCallback(() => {
    mutate(undefined, {
      onError: async (error) => {
        const path = await getErrorPath(error);

        if (path) {
          toast({ description: t('periodicReview.summary.incompleteSection') });
          navigate(path, { replace: true });

          return;
        }

        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
    });
  }, [mutate, navigate, t, toast]);

  return { confirm, isPending };
};
